import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/return-await */
import { useFormContext, } from 'react-hook-form';
import { useCallback, useState, } from 'react';
import { getYear, getMonth, isToday, isFuture, format, } from 'date-fns';
import { addYears, getDaysInMonth, } from 'date-fns/fp';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import DatePicker from 'react-datepicker';
import { DefaultHeader } from '@genericBookings/components/CustomDatepicker/components/headers';
import indepthDatepickerDay from '@genericBookings/components/IndepthDatepicker/Components/IndepthDatepickerDay';
import defaultDatepickerArgs from '@genericBookings/components/CustomDatepicker/defaults';
import useVisitors from '@genericBookings/hooks/useVisitors';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { getActivities, getSelectedActivities, readSessions, } from '@genericBookings/store/reducers/activities';
import { useContext } from 'preact/compat';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import { useFieldsWrapper } from '@genericBookings/context/FieldsWrapper';
import useSessionRefresh from '@genericBookings/hooks/useSessionRefresh';
import { LoadingContent, LoadingCopy, LoadingTitle, Wrapper, } from './styles';
function IndepthDatepicker(props) {
    const { productId, shouldCheck, selectedDate: initialSelectedDate, endpoint, options, } = props;
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
    const { visitors } = useVisitors();
    const { getValues, setValue } = useFormContext();
    const dispatch = useAppDispatch();
    const { isBuilder, isMulti } = useContext(options);
    const activitiesSelector = isBuilder ? getSelectedActivities : getActivities;
    const activities = useAppSelector(activitiesSelector);
    const values = getValues();
    const { setDatepickerOpen, setReservationFailed } = useFieldsWrapper();
    const resetSessions = useSessionRefresh(options);
    const handleMonthOrDateChanged = useCallback(async (theDate) => {
        if (shouldCheck) {
            setLoading(true);
            const daysInMonth = getDaysInMonth(theDate);
            const month = getMonth(theDate);
            const year = getYear(theDate);
            // get every day of month of the given month
            const daysOfMonth = Array.from({ length: daysInMonth }, (i, day) => new Date(`${year}-${month + 1}-${day + 1}`));
            // get all the days that are either today, or in the future
            const futureDaysOfMonth = daysOfMonth.filter((date) => isFuture(date) || isToday(date));
            // get all days in the past so we can later assign as false.
            const pastDays = daysOfMonth.filter((date) => !futureDaysOfMonth.includes(date));
            // dates with no sessions, to be filtered out later
            const daysToCheck = futureDaysOfMonth;
            const fromDate = daysToCheck.at(0); // first day of the month
            const toDate = daysToCheck.at(-1); // hopefully last day of the month
            // Quick check of all available sessions to not waste time on the more resource intensive check availability...
            if (visitors && fromDate && toDate) {
                await endpoint(productId, `${format(fromDate, 'y-MM-dd')}`, `${format(toDate, 'y-MM-dd')}`, visitors)
                    .then((res) => {
                    const foundDates = res; // should always be string array as rejecting stock check
                    // if we sent visitors over this actually performs an indepth date for all dates within
                    // the month
                    const dates = futureDaysOfMonth.map((futureDay) => ({
                        date: futureDay,
                        available: foundDates.includes(`${format(futureDay, 'y-MM-dd')}`),
                    }));
                    const availableDates = dates.filter((potentialDate) => potentialDate.available);
                    const unavailableDates = [...pastDays.map((potentialDate) => ({ date: potentialDate, available: false })), ...dates.filter((potentialDate) => !potentialDate.available)];
                    setDays([...availableDates, ...unavailableDates]);
                })
                    .finally(() => {
                    setLoading(false);
                });
            }
            else {
                setLoading(false);
            }
        }
        else {
            setLoading(false);
        }
    }, [endpoint, productId, shouldCheck, visitors]);
    const handleDidMount = useCallback(() => {
        const visitDate = new Date(getValues('VisitDate'));
        if (visitDate) {
            setSelectedDate(visitDate);
        }
        handleMonthOrDateChanged(selectedDate);
    }, [getValues, handleMonthOrDateChanged, selectedDate]);
    useDidMount(handleDidMount);
    const formatDate = useCallback((date) => format(date, 'yyyy-MM-dd'), []);
    // TODO: add another calendar below with the valid dates selected and change the above when done...
    return (_jsxs(Wrapper, { children: [loading && (_jsxs(LoadingContent, { children: [_jsx(LoadingSpinner, { colourOverride: "#00AEEF" }), _jsx(LoadingTitle, { children: "One second!" }), _jsx(LoadingCopy, { children: "We're finding new dates..." })] })), !loading && (_jsx("div", { children: _jsx("div", { id: "indepth-date-wrapper", className: "datepicker-wrappers", children: _jsx(DatePicker, { ...defaultDatepickerArgs, name: "test", id: "test", onMonthChange: (date) => {
                            setSelectedDate(date);
                            handleMonthOrDateChanged(date);
                        }, onYearChange: (date) => {
                            setSelectedDate(date);
                            handleMonthOrDateChanged(date);
                        }, minDate: new Date(), maxDate: addYears(1, new Date()), selected: selectedDate, onChange: async (date, event) => {
                            if (date) {
                                const updatedActivities = await resetSessions(false);
                                setDatepickerOpen(false);
                                const formattedDate = formatDate(date);
                                setValue('VisitDate', formattedDate);
                                await dispatch(readSessions({
                                    activities: updatedActivities, date: formattedDate, productId, values, isMulti,
                                }));
                                setReservationFailed(false);
                            }
                        }, openToDate: selectedDate, filterDate: (date) => {
                            if (shouldCheck) {
                                const selectedDay = days.find((dayInstance) => {
                                    if (!dayInstance.date) {
                                        return false;
                                    }
                                    // doing this to prevent issue with timezones that was previously occuring...
                                    return format(date, 'y-MM-dd') === format(dayInstance.date, 'y-MM-dd');
                                });
                                // console.log(`${date.toString()} - Found `, selectedDay);
                                return selectedDay ? selectedDay.available : false;
                            }
                            return true;
                        }, renderDayContents: indepthDatepickerDay, renderCustomHeader: (headerProps) => _jsx(DefaultHeader, { ...headerProps }) }) }) }))] }));
}
IndepthDatepicker.defaults = {
    shouldCheck: true,
};
export default IndepthDatepicker;
